import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { FioriPageHeader } from "@/components/fiori";
import { 
  Plus, 
  Edit2, 
  Trash2, 
  AlertCircle,
  Search,
  Bell,
  Settings,
  Check,
  Power,
  AlertTriangle
} from "lucide-react";
import { trpc } from "@/utils/trpc";
import { SecurityAlertConfigModal } from "@/components/SecurityAlertConfigModal";

export function SecurityAlertConfigPage() {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState<number | undefined>();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterActive, setFilterActive] = useState<boolean | undefined>();

  // Queries
  const { data: configs, isLoading, refetch } = trpc.alertConfig.getConfigs.useQuery({
    isActive: filterActive,
    limit: 100,
  });

  // Mutations
  const deleteConfig = trpc.alertConfig.deleteConfig.useMutation({
    onSuccess: () => {
      refetch();
      alert(t("alertConfig.deleted", "Alert configuration deleted"));
    },
  });

  // Filter configs based on search term
  const filteredConfigs = configs?.configs.filter((config) =>
    config.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    config.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const handleEdit = (configId: number) => {
    setSelectedConfigId(configId);
    setModalOpen(true);
  };

  const handleDelete = (configId: number) => {
    if (confirm(t("alertConfig.deleteConfirm", "Are you sure you want to delete this alert configuration?"))) {
      deleteConfig.mutate({ id: configId });
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedConfigId(undefined);
    refetch();
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical":
        return "bg-[#FFE5E5] text-[#FF6B6B]";
      case "high":
        return "bg-[#FFE5E5] text-[#DC2626]";
      case "medium":
        return "bg-[#FEF3C7] text-[#D97706]";
      case "low":
        return "bg-[#DBEAFE] text-[#2563EB]";
      default:
        return "bg-[#F5F5F5] text-[#6B6B6B]";
    }
  };

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6">
      {/* Header */}
      <FioriPageHeader
        title={t("alertConfig.title", "Security Alert Configuration")}
        subtitle={t("alertConfig.subtitle", "Create and manage security alert configurations with trigger conditions, actions, and notifications")}
        icon={<Bell className="h-5 w-5" />}
        actions={
          <Button
            onClick={() => {
              setSelectedConfigId(undefined);
              setModalOpen(true);
            }}
            className="gap-2 bg-[#5B2C93] hover:bg-[#4A2380] text-white"
          >
            <Plus className="w-4 h-4" />
            {t("alertConfig.newConfig", "New Alert Configuration")}
          </Button>
        }
      />

      {/* Alert Modal */}
      <SecurityAlertConfigModal
        open={modalOpen}
        onOpenChange={handleModalClose}
        configId={selectedConfigId}
      />

      {/* KPI Summary Boxes */}
      {configs && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-0 pb-4">
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3 transition-all hover:border-[#5B2C93]/30">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-[#E8DCF5] flex items-center justify-center">
                <Settings className="h-4 w-4 text-[#5B2C93]" />
              </div>
              <div>
                <p className="text-xs text-[#6B6B6B] font-medium">{t("alertConfig.totalConfigurations", "Total Configurations")}</p>
                <p className="text-xl font-semibold text-[#2C2C2C]">{configs.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3 transition-all hover:border-[#5B2C93]/30">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-[#D1FAE5] flex items-center justify-center">
                <Check className="h-4 w-4 text-[#059669]" />
              </div>
              <div>
                <p className="text-xs text-[#6B6B6B] font-medium">{t("alertConfig.active", "Active")}</p>
                <p className="text-xl font-semibold text-[#2C2C2C]">{configs.configs.filter(c => c.isActive).length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3 transition-all hover:border-[#5B2C93]/30">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                <Power className="h-4 w-4 text-[#2563EB]" />
              </div>
              <div>
                <p className="text-xs text-[#6B6B6B] font-medium">{t("alertConfig.enabled", "Enabled")}</p>
                <p className="text-xl font-semibold text-[#2C2C2C]">{configs.configs.filter(c => c.isEnabled).length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3 transition-all hover:border-[#5B2C93]/30">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-[#FFE5E5] flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-[#FF6B6B]" />
              </div>
              <div>
                <p className="text-xs text-[#6B6B6B] font-medium">{t("alertConfig.critical", "Critical")}</p>
                <p className="text-xl font-semibold text-[#2C2C2C]">{configs.configs.filter(c => c.impactLevel === "critical").length}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="px-0 pb-4">
        <div className="bg-white rounded-lg border border-[#E0E0E0] p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide">{t("common.search", "Search")}</label>
              <div className="relative mt-1">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <Input
                  placeholder={t("alertConfig.searchPlaceholder", "Search by name or description...")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="ps-10 border-[#E0E0E0] focus:ring-[#5B2C93]"
                />
              </div>
            </div>
            <div className="flex gap-2 sm:items-end">
              <Button
                variant={filterActive === undefined ? "default" : "outline"}
                size="sm"
                className={`flex-1 sm:flex-none ${filterActive === undefined ? "bg-[#5B2C93] text-white hover:bg-[#4A2380]" : "border-[#E0E0E0] text-[#6B6B6B]"}`}
                onClick={() => setFilterActive(undefined)}
              >
                {t("common.all", "All")}
              </Button>
              <Button
                variant={filterActive === true ? "default" : "outline"}
                size="sm"
                className={`flex-1 sm:flex-none ${filterActive === true ? "bg-[#5B2C93] text-white hover:bg-[#4A2380]" : "border-[#E0E0E0] text-[#6B6B6B]"}`}
                onClick={() => setFilterActive(true)}
              >
                {t("common.active", "Active")}
              </Button>
              <Button
                variant={filterActive === false ? "default" : "outline"}
                size="sm"
                className={`flex-1 sm:flex-none ${filterActive === false ? "bg-[#5B2C93] text-white hover:bg-[#4A2380]" : "border-[#E0E0E0] text-[#6B6B6B]"}`}
                onClick={() => setFilterActive(false)}
              >
                {t("common.inactive", "Inactive")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Configurations List */}
      <div className="px-0">
        {isLoading ? (
          <div className="bg-white rounded-lg border border-[#E0E0E0] p-6">
            <p className="text-center text-[#6B6B6B]">{t("alertConfig.loading", "Loading configurations...")}</p>
          </div>
        ) : filteredConfigs.length === 0 ? (
          <div className="bg-white rounded-lg border border-[#E0E0E0] p-6">
            <div className="flex items-center gap-3 text-[#6B6B6B]">
              <AlertCircle className="h-5 w-5 text-[#9CA3AF]" />
              <p>{t("alertConfig.empty", "No alert configurations found. Create one to get started.")}</p>
            </div>
          </div>
        ) : (
          <ScrollArea className="h-[600px] pr-2">
            <div className="space-y-3">
              {filteredConfigs.map((config) => (
                <div key={config.id} className="bg-white rounded-lg border border-[#E0E0E0] hover:border-[#5B2C93]/30 transition-all overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="text-sm font-semibold text-[#2C2C2C] truncate">{config.name}</h3>
                          <Badge className={`${getSeverityColor(config.impactLevel)} rounded font-medium text-xs`}>
                            {t(`severityLevels.${config.impactLevel}`, config.impactLevel)}
                          </Badge>
                          {config.isActive ? (
                            <Badge className="bg-[#D1FAE5] text-[#059669] rounded font-medium text-xs">{t("common.active", "Active")}</Badge>
                          ) : (
                            <Badge className="bg-[#F5F5F5] text-[#6B6B6B] border border-[#E0E0E0] rounded font-medium text-xs">{t("common.inactive", "Inactive")}</Badge>
                          )}
                          {config.isEnabled ? (
                            <Badge className="bg-[#E8DCF5] text-[#5B2C93] rounded font-medium text-xs">{t("alertConfig.enabled", "Enabled")}</Badge>
                          ) : (
                            <Badge className="bg-[#F5F5F5] text-[#6B6B6B] border border-[#E0E0E0] rounded font-medium text-xs">{t("alertConfig.disabled", "Disabled")}</Badge>
                          )}
                        </div>
                        {config.description && (
                          <p className="text-xs text-[#6B6B6B] mt-1">{config.description}</p>
                        )}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-[#6B6B6B] hover:text-[#5B2C93] hover:bg-[#E8DCF5]"
                          onClick={() => handleEdit(config.id)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-[#6B6B6B] hover:text-[#FF6B6B] hover:bg-[#FFE5E5]"
                          onClick={() => handleDelete(config.id)}
                          disabled={deleteConfig.isPending}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs mt-3 pt-3 border-t border-[#F0F0F0]">
                      <div>
                        <span className="text-[#9CA3AF]">{t("alertConfig.createdBy", "Created by:")} </span>
                        <p className="font-medium text-[#2C2C2C]">{config.createdByName}</p>
                      </div>
                      <div>
                        <span className="text-[#9CA3AF]">{t("alertConfig.createdAt", "Created at:")} </span>
                        <p className="font-medium text-[#2C2C2C]">
                          {new Date(config.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <span className="text-[#9CA3AF]">{t("alertConfig.statusOnTrigger", "Status on Trigger:")} </span>
                        <p className="font-medium text-[#2C2C2C]">{config.statusOnTrigger}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}
